import { useEffect, useRef, useState } from 'react'
import type { GameCalibration } from '@/lib/gameMap'
import { pointText } from '@/lib/gameMap'
import { worldToGame } from '@/lib/mapScale'

/**
 * Izgaranın DOM tarafı: yalnızca imlecin oyun koordinatı, sağ altta.
 * Çizgiler ve etiketler Fabric tuvaline çizilir (bkz. `gridCanvas.ts`).
 *
 * Biçim hesap makinesininkiyle ve oyunun sağ tık okumasıyla aynı
 * (`x72.45, y31.20`). Canlı viewport kapsayıcının satır içi stilinden okunur
 * (bkz. `lib/liveViewport.ts`); durum yalnızca imleç kıpırdayınca güncellenir,
 * zoom sırasında render yok. İmleç oynanabilir alanın dışındayken basılmaz.
 */

interface Props {
  worldSize: number
  cal: GameCalibration
}

export default function GridLayer({ worldSize, cal }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [cursorGame, setCursorGame] = useState<{ x: number; y: number } | null>(null)
  useEffect(() => {
    const host = (ref.current?.closest('[data-vpt-host]') as HTMLElement | null) ?? ref.current?.parentElement
    if (!host) return
    const num = (name: string, fb: number) => {
      const v = parseFloat(host.style.getPropertyValue(name))
      return Number.isFinite(v) ? v : fb
    }
    const onMove = (e: PointerEvent) => {
      const r = host.getBoundingClientRect()
      const a = num('--va', 1), ve = num('--ve', 0), vf = num('--vf', 0)
      setCursorGame(worldToGame({ x: (e.clientX - r.left - ve) / a, y: (e.clientY - r.top - vf) / a }, cal, worldSize))
    }
    const onLeave = () => setCursorGame(null)
    host.addEventListener('pointermove', onMove)
    host.addEventListener('pointerleave', onLeave)
    return () => {
      host.removeEventListener('pointermove', onMove)
      host.removeEventListener('pointerleave', onLeave)
    }
  }, [cal, worldSize])
  const b = cal.bounds
  const inside = cursorGame
    && cursorGame.x >= b.minX && cursorGame.x <= b.maxX
    && cursorGame.y >= b.minY && cursorGame.y <= b.maxY

  return (
    <div ref={ref} className="pointer-events-none absolute bottom-3 right-3 z-20">
      {inside && cursorGame && (
        <div className="rounded-sm border border-white/10 bg-black/75 px-2.5 py-1 font-mono text-xs tabular-nums text-white/90">
          {pointText(cursorGame)}
        </div>
      )}
    </div>
  )
}
